import type { VerificationQuestionView } from "@credit-acceptance/step6-bff-client";

import { RESIDENCE_CALL_SCRIPT_QUESTION_TEXT, type ResidenceQuestionFieldKey } from "./callScriptResidence.types";

// ---------------------------------------------------------------------------
// callScriptResidenceQuestions
//
// Residence counterpart of callScriptQuestions.ts. The catalog returned by
// `getResidenceCallScriptQuestions(approvalId, applicantId, type)` is matched against
// RESIDENCE_CALL_SCRIPT_QUESTION_TEXT by question text to resolve each
// field's `verificationQuestionPk` for the save body's `verificationAnswers[]`.
//
// Unlike the employment script there is no income slot: the residence script
// has exactly one question set per applicant role.
// ---------------------------------------------------------------------------

/** Field key -> resolved `verificationQuestionPk`. */
export type ResidenceQuestionPkMap = Partial<Record<ResidenceQuestionFieldKey, number>>;

/**
 * Resolves the `type` code for the residence questions catalog lookup:
 * `CVDQ` for the driver, `CVSSQ` for the second signer (CAPS2's Customer
 * Verification driver / second-signer question sets).
 * @param {"driver" | "secondSigner"} personPath - Which applicant.
 * @returns {string} The `type` query value for `getResidenceCallScriptQuestions`.
 */
export function resolveResidenceVerificationQuestionType(personPath: "driver" | "secondSigner"): string {
  return personPath === "driver" ? "CVDQ" : "CVSSQ";
}

/**
 * Every `type` code the residence Call Script modal can request. Global
 * reference data — safe to prefetch and cache in full up front.
 */
export const ALL_RESIDENCE_CALL_SCRIPT_QUESTION_TYPES: readonly string[] = (["driver", "secondSigner"] as const).map(
  (personPath) => resolveResidenceVerificationQuestionType(personPath)
);

/**
 * Builds a field-key -> verificationQuestionPk lookup by matching each
 * field's question text (case-insensitively) against the fetched catalog.
 * Fields whose text isn't in the catalog are left out — no answer row is
 * built for them.
 * @param {VerificationQuestionView[]} questions - Catalog entries for the role's `type`.
 * @returns {ResidenceQuestionPkMap} Resolved pks, keyed by field.
 */
export function buildResidenceQuestionPkMap(questions: VerificationQuestionView[]): ResidenceQuestionPkMap {
  const byLowerText = new Map<string, number>();
  for (const question of questions) {
    if (question.question && question.verificationQuestionPk !== undefined) {
      byLowerText.set(question.question.toLowerCase(), question.verificationQuestionPk);
    }
  }

  const pkMap: ResidenceQuestionPkMap = {};
  for (const [field, text] of Object.entries(RESIDENCE_CALL_SCRIPT_QUESTION_TEXT) as Array<
    [ResidenceQuestionFieldKey, string]
  >) {
    const pk = byLowerText.get(text.toLowerCase());
    if (pk !== undefined) {
      pkMap[field] = pk;
    }
  }
  return pkMap;
}
